/**
 * Trend chart — UX/UI Specification §13.3.
 *
 * SVG rather than canvas: canvas rasterises, and a rasterised chart sitting
 * next to vector text in an A4 PDF is visibly soft. The same component draws
 * the Measurements tab and, from Sprint 4, the report.
 *
 * No smoothing. A spline through sparse body measurements invents values
 * between the points that were never taken.
 */

import { formatValue, type ChartData } from '@pp/shared';
import './Chart.css';

const WIDTH = 320;
const HEIGHT = 158;

/**
 * Room for the outermost labels, so nothing is clipped at the edges of the
 * viewBox (Doc 6 §"Draw charts to the scale"). The top gap holds the value
 * label that sits above the highest point.
 */
const PAD = { top: 24, right: 20, bottom: 24, left: 40 };

/**
 * Anchors the first and last label to the inside of the plot.
 *
 * Centred, the first label runs left over the axis numbers and the last runs
 * off the right edge — "Ağustos" was rendering as "Ağusto".
 */
function anchorFor(index: number, count: number): 'start' | 'middle' | 'end' {
  if (index === 0) return 'start';
  if (index === count - 1) return 'end';
  return 'middle';
}

interface ChartProps {
  chart: ChartData;
}

export function Chart({ chart }: ChartProps) {
  const plotWidth = WIDTH - PAD.left - PAD.right;
  const plotHeight = HEIGHT - PAD.top - PAD.bottom;
  const span = chart.yMax - chart.yMin || 1;

  // The x-axis is ordinal, not time-proportional: measurement dates are
  // irregular by design, and spacing them by time would draw clusters and
  // voids that read as data (§13.2).
  const x = (index: number) =>
    chart.points.length === 1
      ? PAD.left + plotWidth / 2
      : PAD.left + (index / (chart.points.length - 1)) * plotWidth;

  const y = (value: number) =>
    PAD.top + plotHeight - ((value - chart.yMin) / span) * plotHeight;

  const gridValues: number[] = [];
  for (let v = chart.yMin; v <= chart.yMax + 1e-9; v += chart.yStep) {
    gridValues.push(Number(v.toFixed(6)));
  }

  const line = chart.points.map((p, i) => `${x(i)},${y(p.value)}`).join(' ');
  const title = chart.unit ? `${chart.label} (${chart.unit})` : chart.label;

  const summary = `${chart.label}: ${chart.points
    .map((p) => `${p.axisLabel} ${formatValue(p.value, chart.unit, chart.decimals)}`)
    .join(', ')}`;

  return (
    <figure className="chart">
      <figcaption className="chart__title">{title}</figcaption>
      <svg
        className="chart__svg"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role="img"
        aria-label={summary}
        preserveAspectRatio="xMidYMid meet"
      >
        {gridValues.map((value) => (
          <g key={value}>
            <line
              x1={PAD.left}
              x2={WIDTH - PAD.right}
              y1={y(value)}
              y2={y(value)}
              className="chart__grid"
            />
            <text x={PAD.left - 6} y={y(value) + 3} className="chart__tick">
              {formatValue(value, '', chart.decimals)}
            </text>
          </g>
        ))}

        <polyline points={line} className="chart__line" fill="none" />

        {chart.points.map((point, index) => {
          const anchor = anchorFor(index, chart.points.length);
          return (
            <g key={point.measuredAt + index}>
              <circle cx={x(index)} cy={y(point.value)} r={3} className="chart__point" />
              <text
                x={x(index)}
                y={y(point.value) - 8}
                textAnchor={anchor}
                className="chart__value"
              >
                {formatValue(point.value, '', chart.decimals)}
              </text>
              <text
                x={x(index)}
                y={HEIGHT - 6}
                textAnchor={anchor}
                className="chart__axis"
              >
                {point.axisLabel}
              </text>
            </g>
          );
        })}
      </svg>
    </figure>
  );
}
